import { forumThreadPolicy } from '@/policies';
import { ForumThreadModel } from '@/models';
import { ForumChannel } from '@/services/channels/forum/channel';
import { ForumCommentCreated, ForumCommentEdited, ForumCommentUpdated } from '@/services/channels/forum/broadcasts';
import { iForumThread } from '@/interfaces/forum';
import { route } from '@/services';

(function (angular) {
    angular.module('app')
        .directive('forumThread', forumThread);

    forumThread.$inject = [
        '$q',
        '$rootScope',
        'ForumCategoriesApi',
        'ForumLikesApi',
        'ForumRepliesApi',
        'ForumThreadsApi',
        'thisUser',
        'mimetypesPayload',
        'alerts',
        'UploadsApi',
        'thisSiteSettings',
        'PollsApi',
    ];

    function forumThread(
        $q,
        $rootScope,
        ForumCategoriesApi,
        ForumLikesApi,
        ForumRepliesApi,
        ForumThreadsApi,
        thisUser,
        mimetypesPayload,
        alerts,
        UploadsApi,
        thisSiteSettings,
        PollsApi,
    ) {

        var link = function ($scope) {

            // properties
            $scope.policyChecks = getPolicyChecks();
            $scope.input = {};
            $scope.category = $scope.category || {};
            $scope.thread = $scope.thread || {
                body: '',
                entities: [],
                private: false,
                thread: '',
                uploads: [],
                upload_files: {
                    images: [],
                    audio: [],
                    documents: [],
                    videos: [],
                },
            };
            $scope.noReplies = $scope.noReplies || false;
            $scope.fileUploadOptions = $scope.fileUploadOptions || {
                filePattern: '*',
                maxFilesize: 256 * 1024 * 1024, // 256 MB
                dragOverClass: 'dragging',
            };

            $scope.deletingReply = 0;
            $scope.isDeleting = false;
            $scope.isLoading = false;
            $scope.isEditing = $scope.isEditing || false;
            $scope.isExpanded = false;
            $scope.user = thisUser;
            $scope.displayComments = false;
            $scope.thisSiteSettings = thisSiteSettings;

            // methods
            $scope.deleteThread = deleteThread;
            $scope.deleteThreadApi = deleteThreadApi;
            $scope.deleteThreadCancel = deleteThreadCancel;
            $scope.editThread = editThread;
            $scope.expand = expand;
            $scope.formatBody = formatBody;
            $scope.limitTo = limitTo;
            $scope.saveThread = saveThread;
            $scope.removeFile = removeFile;
            $scope.setExpanded = setExpanded;
            $scope.toggleLike = toggleLike;
            $scope.toggleSticky = toggleSticky;
            $scope.updateCategory = updateCategory;
            $scope.updateComments = updateComments;
            $scope.onFileSelected = onFileSelected;
            $scope.onFileSelectedError = onFileSelectedError;
            $scope.isPosting = false;
            $scope.toggleCommentsDisplay = toggleCommentsDisplay;

            // We're not interesting in subscribing if we don't have a thread ID.
            if ($scope.thread.id) {
                ForumChannel.subscribe(ForumCommentCreated, (broadcast) => commentChangedHandler(broadcast));
                ForumChannel.subscribe(ForumCommentEdited, (broadcast) => commentChangedHandler(broadcast));
                ForumChannel.subscribe(ForumCommentUpdated, (broadcast) => commentChangedHandler(broadcast));
            }

            function commentChangedHandler(
                broadcast: ForumCommentCreated | ForumCommentEdited | ForumCommentUpdated,
            ): void {
                // We can't do anything until we have replies.
                if (!$scope.replies) {
                    return;
                }

                // We're not interested in replies that don't belong to us.
                if (broadcast.data.data.forum_thread_id != $scope.thread.id) {
                    return;
                }

                // We're not interested in comments that have a parent, because the heartbeat
                // endpoint returns nested comments when a parent comment is updated.
                if (broadcast.data.data.parent_id !== null) {
                    return;
                }

                const replyIndex = $scope.replies.shown.findIndex(reply => reply.id === broadcast.data.data.id);

                // If we didn't find a reply.
                if (replyIndex === -1) {
                    // We don't create unless that's the broadcast we received.
                    if (broadcast.constructor !== ForumCommentCreated) {
                        return;
                    }

                    // Add the created broadcast in.
                    const shown = $scope.replies.shown.concat([broadcast.data.data]);
                    let omitted = $scope.replies.omitted;

                    // Handle limited number of shown replies.
                    if ($scope.numReplies && shown.length > $scope.numReplies) {
                        shown.shift();
                        omitted++;
                    }

                    // Show replies.
                    $scope.replies = { shown, omitted };
                } else {
                    $scope.replies.shown[replyIndex] = broadcast.data.data;
                }
            }

            /**
             * Initialises the controller
             *
             * @returns {void}
             */
            function bootstrap(): void {
                $rootScope.$on('forum:updateReplies', function ($event, threadId) {
                    if ($scope.thread.id === threadId) {
                        updateComments();
                    }
                });

                // update policy checks when the thread data changes
                $scope.$watch('thread', () => {
                    $scope.policyChecks = getPolicyChecks();
                });

                update();
                getPolls();
            }

            /**
             * Show the delete thread modal/are you sure question.
             *
             * @return {void}
             */
            function deleteThread() {
                $scope.isDeleting = true;
            }

            /**
             * Delete thread when the yes button is clicked.
             *
             * @return {void}
             */
            function deleteThreadApi() {
                return $q(function (resolve) {
                    ForumThreadsApi.delete({ threadId: $scope.thread.id }, function () {
                        $rootScope.$emit('forum:updateThreads');
                        resolve();
                    });
                });
            }

            /**
             * Delete thread modal cancel button clicked.
             *
             * @return {void}
             */
            function deleteThreadCancel() {
                $scope.isDeleting = false;
            }

            /**
             * Start editing the thread content. When editing, only the body
             * should be editable.
             *
             * @return {void}
             */
            function editThread() {
                $scope.isEditing = true;
                $scope.isExpanded = true;
            }

            /**
             * Expand the edit box outwards.
             *
             * @return {void}
             */
            function expand() {
                $scope.isExpanded = true;
            }

            /**
             * Format the body - add links, create breaks where newlines are.
             *
             * @return {String}
             */
            function formatBody() {
                let formatted = new ForumThreadModel($scope.thread).expandEntities();
                formatted = formatted + '\r\n'; // for some reason the last line is cut off without adding this to the end
                formatted = formatted.replace(/(https?|ftps?)\:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,3}(\/\S*)?/g, '<a href="$&">$&</a>');
                formatted = formatted.replace(/(\r\n|\n|\r)/gm, '<br>');
                return formatted;
            }

            /**
             * Gets an object of caches policy checks.
             *
             * @returns {Record<string, boolean>}
             */
            function getPolicyChecks(): Record<string, boolean> {
                try {
                    return {
                        canCreate: forumThreadPolicy.canCreate(),
                        canCreatePrivate: forumThreadPolicy.canCreatePrivate(),
                        canDelete: forumThreadPolicy.canDelete(new ForumThreadModel($scope.thread)),
                        canManage: forumThreadPolicy.canManage(new ForumThreadModel($scope.thread)),
                        canToggleSticky: forumThreadPolicy.canToggleSticky(new ForumThreadModel($scope.thread)),
                    };
                } catch (err) {
                    console.warn(err);
                }
            }

            /**
             * Nicely limit a string of text, with optional formatting. This is
             * useful for if, in the future, we wanted to render the html and then
             * have us essentially limit the text, preserving formatting.
             *
             * @param  {String} text
             * @param  {Number} limitTo
             * @return {String}
             */
            function limitTo(text, entities, limitTo) {
                // Make substitutions to make the text nicer (adding links, creating breaks).
                var formatted = formatBody();
                // Essentially what we're doing here is being awfully nice to the
                // rendering engine and telling it to end our tags for us.
                var $el = document.createElement('div');
                $el.innerHTML = formatted.substr(0, limitTo) + (formatted.length > limitTo ? '...' : '');
                $el.innerHTML = $el.innerHTML.replace(/^(.*)\<\/?.+$/, '$1');
                return $el.innerHTML + ((formatted.length > limitTo) ? '<br><a class="read-more-link" href="/forum/' + $scope.category.slug + '/' + $scope.thread.slug + '">Read more &raquo;</a>' : '');
            }

            function removeFile(category, file) {
              $scope.thread.upload_files[category].splice($scope.thread.upload_files[category].indexOf(file), 1);
              UploadsApi.removeFile(file.path, file.id);
            }

            /**
             * Save the thread - if the thread already exists don't create, but
             * update.
             *
             * @return {void}
             */
            function saveThread() {
                const body = {
                    body: $scope.thread.body,
                    entities: $scope.thread.entities.map(entity => {
                        delete entity.entity;
                        return entity;
                    }),
                    title: $scope.thread.title,
                    tags: $scope.thread.tags || [],
                    is_sticky: $scope.thread.is_sticky || false,
                    forum_category_id: $scope.category.id,
                    uploads: $scope.thread.uploads || [],
                    upload_files: $scope.thread.upload_files || {
                        images: [],
                        audio: [],
                        documents: [],
                        videos: [],
                    },
                    private: $scope.thread.private || false,
                    poll_id: $scope.thread.poll_id || null,
                };

                $scope.isPosting = true;

                if ($scope.thread.id) {
                    ForumThreadsApi.update({ threadId: $scope.thread.id }, body, function (thread) {
                        $scope.isEditing = false;
                        $scope.isExpanded = false;
                        $scope.isPosting = false;

                        $scope.thread = thread.data;
                    });
                } else {
                    ForumThreadsApi.save({}, body, function (response: { data: iForumThread }) {
                        // Redirect to the new thread if it's private
                        if (response.data.private) {
                            return window.location.href = route('forum.thread').make(new ForumThreadModel(response.data));
                        }

                        $scope.isExpanded = false;
                        $scope.isPosting = false;

                        $scope.thread = {title: '', body: '', entities: []};
                        $rootScope.$emit('forum:updateThreads');
                    });
                }

            }

            /**
             * Set the isExpanded variable.
             *
             * @return {Boolean}
             */
            function setExpanded(expanded) {
                $scope.isExpanded = expanded;
            }

            /**
             * Toggle the liked status of the thread.
             *
             * @return {void}
             */
            function toggleLike() {
                return $q(function (resolve) {
                    if (thisUser && thisUser.id) {
                        if ($scope.thread.user_liked) {
                            ForumLikesApi.delete({
                                likeId: $scope.thread.user_liked
                            }, {}, function () {
                                $scope.thread.user_liked = 0;
                                updateLikes().then(resolve);
                            });
                        } else {
                            ForumLikesApi.save({}, {
                                forum_object_id: $scope.thread.id,
                                forum_object_type: 'thread',
                            }, function (like) {
                                $scope.thread.user_liked = like.data.id;
                                updateLikes().then(resolve);
                            });
                        }
                    }
                });
            }

            /**
             * Toggle the sticky status of a thread.
             *
             * @return {void}
             */
            function toggleSticky() {
                return $q(function (resolve) {
                    ForumThreadsApi.update({
                        threadId: $scope.thread.id
                    }, angular.extend($scope.thread, {
                        is_sticky: !$scope.thread.is_sticky
                    }), function (thread) {
                        $scope.thread = thread.data;
                        $rootScope.$emit('forum:updateThreads');
                        resolve();
                    });
                });
            }

            /**
             * Look for information to the parent category, if it's not been provided.
             *
             * @return {void}
             */
            function updateCategory() {
                return $q(function (resolve) {
                    if ($scope.category.id) {
                        resolve();
                    } else {
                        ForumCategoriesApi.get({ categoryId: $scope.thread.forum_category_id }, function (category) {
                            $scope.category = category;
                            resolve();
                        });
                    }
                });
            }

            /**
             * Look for responses to this thread.
             *
             * @return {void}
             */
            function updateComments() {
                return $q(function (resolve) {
                    if ($scope.thread.id) {
                        ForumRepliesApi.query({ forum_thread_id: $scope.thread.id, is_parent: true }, function (replies) {
                            if (!$scope.numReplies) {
                                $scope.replies = {
                                    shown: replies,
                                    omitted: 0,
                                };
                            } else {
                                $scope.replies = {
                                    shown: replies.reverse().slice(0, $scope.numReplies).reverse(),
                                    omitted: replies.length - $scope.numReplies,
                                };
                            }

                            resolve();
                        });
                    } else {
                        resolve();
                    }
                });
            }

            /**
             * Look for likes on this thread.
             *
             * @return {void}
             */
            function updateLikes() {
                return $q(function (resolve) {
                    if ($scope.thread.id) {
                        ForumLikesApi.query({
                            forum_object_id: $scope.thread.id,
                            forum_object_type: 'thread',
                        }, function (likes) {
                            $scope.likes = {
                                shown: likes.data.reverse().slice(0, 5),
                                omitted: likes.data.length - 5,
                            };

                            resolve();
                        });
                    } else {
                        resolve();
                    }
                });
            }

            /**
             * Update the category if we need to, then load comments.
             *
             * @return {void}
             */
            function update() {
                return updateCategory().then(function () {
                    if (!$scope.noReplies) {
                        return updateComments().then(function () {
                            return updateLikes();
                        });
                    }
                });
            }

            /**
             * Caches the file and uploads to the API
             *
             * @param  {String} base64Data - file converted to base64
             * @param  {Object} file   - brower file object
             * @return {Void}
             */
            function onFileSelected(base64Data, file) {
                const filePlaceholder = {
                    client_filename: file.name,
                    isUploading: true,
                    uploadProgress: 0,
                };

                // check file type is valid
                const acceptedFileTypes = Object.keys(mimetypesPayload);
                if (acceptedFileTypes.indexOf(file.type) === -1) {
                    return alerts.emit({
                        type: 'warning',
                        title: 'File Error',
                        message: 'Unsupported file type',
                    });
                }

                // determine category file should be placed into
                const fileType = (mimetypesPayload[file.type] || { type: 'document' }).type;
                const fileTypeCategoryMap = {
                    image: 'images',
                    audio: 'audio',
                    document: 'documents',
                    csv: 'documents',
                    video: 'videos',
                };
                // save reference to the category array that will hold this file
                const fileCategory = $scope.thread.upload_files[fileTypeCategoryMap[fileType]];

                if (!fileCategory) {
                    return alerts.emit({
                        theme: 'error',
                        title: `File type "${fileType}" is not supported`,
                    });
                }

                // add file placeholder to resource to update the view
                fileCategory.push(filePlaceholder);

                // get index of file placeholder
                const fileObjectIndex = fileCategory.length - 1;

                // upload the file
                UploadsApi.uploadFile(file, 'forum', true)
                    .then(
                        function handleUploadFileSuccess(response) {
                            delete fileCategory[fileObjectIndex].isUploading;
                            // save file ID to resource
                            $scope.thread.uploads.push(response.id);
                            // update file placeholder with complete data
                            fileCategory[fileObjectIndex] = response;
                        },
                        function handleUploadFileError() {
                            fileCategory.splice(fileObjectIndex, 1);
                        },
                        function handleUploadProgress(progress) {
                            fileCategory[fileObjectIndex].uploadProgress = progress;
                        }
                    );
            }

            function onFileSelectedError(error) {
                alerts.emit({
                    theme: 'error',
                    title: 'This file could not be uploaded',
                    message: error.image,
                });
            }

            function toggleCommentsDisplay() {
                $scope.displayComments = $scope.displayComments ? false : true;
            }

            /**
             * Retrieves polls
             *
             * @return {void}
            */
            function getPolls() {
                PollsApi.get({
                    status: 'approved',
                }, response => {
                    $scope.polls = response.data;
                });
            }

            // init
            bootstrap();
        };

        return {
            restrict: 'E',
            templateUrl: 'directives/forumThread.html',
            transclude: true,
            link: link,
            scope: {
                numReplies: '=?',
                noReplies: '=?',
                isEditing: '=?',
                category: '=?',
                isSingle: '=?',
                thread: '=?',
            },
        };

    }

}(angular));
